import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { PageId, useSetPriceConfigs } from '@vp/price-platform'
import { useIdentity, useLogger } from '@vp/ubik-context'

import { useAppState } from '../../root/contexts/AppContext'
import { Quantity } from '../'
import { Processing } from '../../processing/Processing'
import { ErrorPage } from '../../errorPage/ErrorPage'
import { useCart } from '../../../hooks/useCart'
import { useAuthorization } from '../../root/contexts/AuthorizationProvider'
import { useTenant } from '../../root/contexts/TenantContext'
import { canAddDirectlyToCart } from '../../../utils/AutoAddToCartHelpers'
import useAvailableColorOptions from '../../../hooks/queries/useAvailableColorOptions'
import { useABTest } from '../../root/contexts/ABTestContext'
import { navigateToNextStep } from '../../../hooks/useAddToCartAction/navigation'
import { navigateToTeamsPage } from '../../../utils/teamsNavigation'
import type { Project } from '../../../types/Project'
import { trackProjectsAddToCart } from '../../../hooks/useAddToCartAction/trackProjectsAddToCart'
import { useTracking } from '../../root/contexts/TrackingContext'

/**
 * Check if a project is a teams project based on metadata.
 * Teams projects have either groupsAndTeamsData or hasTeamsPlaceholders.
 */
const isProjectATeamProject = (project: Project): boolean => {
  const work = project.work
  return !!work?.design?.metadata?.groupsAndTeamsData || work?.design?.metadata?.hasTeamsPlaceholders === 'true'
}

export const QuantityPageWrapper: React.FC = () => {
  const [autoAddToCartEligible, setAutoAddToCartEligible] = useState<boolean | null>(null)

  const { culture, isAppStateInitialized, isCareAgent, initialWorkIds, nextStepUrl, primaryProject, isInError } = useAppState()
  const { upsertCartAsync } = useCart()
  const { authorizationHeader } = useAuthorization()
  const { tenant } = useTenant()
  const { isIdentityInitialized } = useIdentity()
  const { isAvailable } = useABTest()
  const { trackAddToCart } = useTracking()
  const logger = useLogger()

  useSetPriceConfigs({
    locale: culture,
    tenant,
    pageId: PageId.QuantitySelectionPage,
    developmentMode: process.env['NODE_ENV'] === 'development',
  })

  const { data: availableColorOptions, isFetched: hasLoadedColorOptions } = useAvailableColorOptions()
  const isTeamsProject = useMemo(() => (primaryProject ? isProjectATeamProject(primaryProject) : false), [primaryProject])

  // automatically add to cart when there's only 1 work in qsp with valid quantity
  // Use useCallback to properly memoize autoAddToCart with current dependencies
  const autoAddToCart = useCallback(async () => {
    if (!primaryProject) {
      throw new Error('Can not add direct to cart because there is no primary project.')
    }

    if (!authorizationHeader) {
      logger.warn('Cannot auto-add to cart: authorization header is missing', {
        workId: primaryProject.workId,
      })
      setAutoAddToCartEligible(false)
      return
    }

    try {
      await upsertCartAsync(primaryProject.workId, culture, authorizationHeader, logger)
    } catch (err) {
      logger.warn('Failed to add to cart automatically', { error: err, workId: primaryProject.workId, culture })
      setAutoAddToCartEligible(false)
      return
    }

    trackProjectsAddToCart([primaryProject], { [primaryProject.workId]: { inputQuantity: primaryProject.work?.merchandising?.quantity ?? 0 } }, trackAddToCart)

    const nextStepPath = nextStepUrl.replace('{workId}', primaryProject.work.workId)

    navigateToNextStep([primaryProject], nextStepPath, logger)
  }, [primaryProject, authorizationHeader, culture, upsertCartAsync, logger, trackAddToCart, nextStepUrl])

  useEffect(() => {
    /**
     * Determine if this product is eligible to automatically
     * add to cart and redirect the customer to the next step.
     * Skip this check entirely for teams projects since they redirect to teams page.
     */
    if (autoAddToCartEligible !== null) {
      return // we already calculated this
    }

    if (!isAppStateInitialized) {
      return // still loading
    }

    // Check teams project first - if it's a teams project, we don't need to check auto-add eligibility
    if (isTeamsProject) {
      setAutoAddToCartEligible(false)
      return
    }

    if (isCareAgent || initialWorkIds.length > 1) {
      setAutoAddToCartEligible(false) // care agent mode or multiple works are not eligible
      return
    }

    const shouldWaitForColorOptions = isAvailable && !hasLoadedColorOptions
    if (shouldWaitForColorOptions) {
      return // still loading color options, wait until loaded
    }

    const colorOptionsForEligibility = isAvailable ? availableColorOptions : []
    setAutoAddToCartEligible(canAddDirectlyToCart(primaryProject, colorOptionsForEligibility))
  }, [primaryProject, isAppStateInitialized, initialWorkIds, isCareAgent, autoAddToCartEligible, availableColorOptions, hasLoadedColorOptions, isAvailable])

  useEffect(() => {
    if (autoAddToCartEligible && authorizationHeader && isIdentityInitialized) {
      autoAddToCart()
    }
  }, [autoAddToCartEligible, authorizationHeader, isIdentityInitialized, autoAddToCart])

  // Redirect to teams page if this is a teams project
  useEffect(() => {
    if (isTeamsProject && primaryProject) {
      navigateToTeamsPage(primaryProject, logger)
    }
  }, [isTeamsProject, primaryProject, logger])

  if (isInError) {
    return <ErrorPage /> // There was an error loading a work or BFF data.
  }

  if (!isAppStateInitialized || autoAddToCartEligible === null || isTeamsProject) {
    return <Processing />
  }

  if (autoAddToCartEligible) {
    // TODO: add message to indicate adding to cart
    return (
      <>
        <span data-testid="qty-wrapper-add-to-cart"></span>
        <Processing />
      </>
    )
  }

  return <Quantity />
}
